import {PI_CARDINAL} from '../pi/piPaths';
import type {PlushState} from './PaiPlush';
import {PlushFace, PlushFrame} from './plushKit';

/**
 * Puddle: a squat green frog plush under a little cardinal umbrella —
 * always ready for a 70% chance of rain. Beady eyes sit up in two eye
 * bumps, a pale belly, and wide webbed feet.
 */

const FUR = {light: '#A6D986', mid: '#6DB257', dark: '#4C8C3E'};
const BELLY = '#E6F2C4';
const FEET = '#5A9E48';
const HANDLE = '#6E4A2F';
const CANOPY_STRIPE = '#FFF1E6';

/** Frog body: wider and squatter than the bean, with two eye bumps. */
const BODY =
  'M 46 70 C 46 44 94 44 94 64 C 96 60 104 60 106 64 C 106 44 154 44 154 70 ' +
  'C 178 86 186 118 184 146 C 182 180 150 196 100 196 C 50 196 18 180 16 146 ' +
  'C 14 118 22 86 46 70 Z';

/** Props for {@link PuddlePlush}. */
export interface PuddlePlushProps {
  /** Rendered width in px. */
  size: number;
  /** Expression to show. Omitted defaults to `idle`. */
  state?: PlushState;
}

/**
 * Renders Puddle (see module comment). Decorative (`aria-hidden`).
 *
 * @param props - See {@link PuddlePlushProps}.
 * @return The mascot SVG.
 * @throws Never.
 */
export function PuddlePlush({size, state = 'idle'}: PuddlePlushProps) {
  return (
    <PlushFrame
      size={size}
      state={state}
      furStops={FUR}
      eyeLineFraction={70 / 220}
      thoughtAnchor={{x: 40, y: 24}}
    >
      {kit => (
        <>
          <g filter={kit.fuzz}>
            <ellipse cx={62} cy={199} rx={26} ry={9} fill={FEET} />
            <ellipse cx={138} cy={199} rx={26} ry={9} fill={FEET} />
            <path d={BODY} fill={kit.fur} />
          </g>
          <ellipse
            cx={100}
            cy={156}
            rx={56}
            ry={34}
            fill={BELLY}
            filter={kit.soft}
          />
          <PlushFace kit={kit} x={100} y={68} scale={1.25} />
          {/* Umbrella: handle in the right hand, canopy over the head. */}
          <path
            d="M 150 150 L 146 48 M 150 150 Q 152 162 144 162"
            stroke={HANDLE}
            strokeWidth={4}
            strokeLinecap="round"
            fill="none"
          />
          <g filter={kit.fuzz}>
            <ellipse cx={152} cy={148} rx={11} ry={10} fill={FEET} />
            <path
              d="M 90 50 C 92 2 200 2 202 50 C 194 43 182 43 174 50 C 166 43 154 43 146 50 C 138 43 126 43 118 50 C 110 43 98 43 90 50 Z"
              fill={PI_CARDINAL}
            />
          </g>
          <path
            d="M 146 6 C 144 24 146 38 146 50 M 146 6 C 162 14 172 32 174 50 M 146 6 C 130 14 120 32 118 50"
            stroke={CANOPY_STRIPE}
            strokeWidth={2}
            opacity={0.5}
            fill="none"
          />
          <circle cx={146} cy={4} r={3.5} fill={HANDLE} />
        </>
      )}
    </PlushFrame>
  );
}
